import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSelector } from 'react-redux'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { Button } from '@mobi/component-library/Base/Buttons'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { CustomerServiceCentre, CustomerServiceTelephone } from '@mobi/component-library/Base/Link'
import { spacing } from '@mobi/component-library/Foundations'
import { callCenterPhoneNumber } from '@mobi/utils/phone'
import { getSignUpState } from '../../Store'
import { trackVerificationContactUs } from '../../../Verification/Utils/ga'
import { trackSignUpVerificationFail } from '../../Utils/ga'
import { routes } from '../../Utils'
import { FormLayout } from '../Verification/Layout.styled'

export const FailedVerification = ({
  showLogin,
}: {
  showLogin: (options?: { onLoginSuccessRedirectUrl?: string }) => void
}) => {
  const navigate = useNavigate()
  const state = useSelector(getSignUpState)
  const [failType, setFailType] = useState<
    | undefined
    | 'closedAccount'
    | 'duplicateAccount'
    | 'sanctioned'
    | 'unverified'
    | 'newUserAutoLogInFail'
  >()
  const [header, setHeader] = useState<string>('')
  const [subHeader, setSubHeader] = useState<React.JSX.Element>()

  const verification = state?.signupVerification
  const applicationReferenceNumber =
    state?.signupVerification?.applicationReferenceNumber?.toString() ?? ''

  useEffect(() => {
    if (verification?.duplicateExists && verification?.isDuplicateClosed) {
      trackSignUpVerificationFail('Close account')
      setFailType('closedAccount')
      setHeader('We were unable to verify your identity.')
      setSubHeader(
        <>
          Unfortunately we couldn't verify your identity with the details you provided, please
          contact us on <CustomerServiceTelephone /> or use the button below.
        </>
      )
    }
    if (verification?.duplicateExists && !verification?.isDuplicateClosed) {
      trackSignUpVerificationFail('Duplicate account')
      setFailType('duplicateAccount')
      setHeader('Looks like you already have an account.')
      setSubHeader(
        <>
          Please login to access your account. If you have any issues accessing your account, please
          contact us on <CustomerServiceTelephone /> or use the button below.
        </>
      )
    }
    if (verification?.potentialSanctionedMatch) {
      trackSignUpVerificationFail('Potential sanctioned')
      setFailType('sanctioned')
      setHeader('We were unable to verify your identity.')
      setSubHeader(
        <>
          Unfortunately we couldn’t verify your identity with the details you provided. We are
          unable to create a TABtouch account for you.
        </>
      )
    }
    if (
      !verification?.isVerified &&
      !verification?.duplicateExists &&
      !verification?.potentialSanctionedMatch
    ) {
      trackSignUpVerificationFail('Unverified')
      setFailType('unverified')
      setHeader('We were unable to verify your identity.')
      setSubHeader(
        <CustomerServiceCentre prefixText='Unfortunately we couldn’t verify your identity with the details you provided. We are unable to create a TABtouch account for you.' />
      )
    }
    if (state?.isNewUserAutoLoggedIn === false) {
      setFailType('newUserAutoLogInFail')
      setHeader('We have created your account, but encountered an issue logging you in.')
      setSubHeader(
        <>
          Please try logging in to set your deposit limit and start betting. For ongoing issues
          please contact us on <CustomerServiceTelephone />.
        </>
      )
    }
  }, [state])

  if (!state?.signupApplication || !state?.signupVerification) {
    return null
  }

  if (
    state.signupVerification.isVerified &&
    !state.signupVerification.duplicateExists &&
    !state.signupVerification.potentialSanctionedMatch &&
    state.isNewUserAutoLoggedIn
  ) {
    return null
  }

  const contactUs = () => {
    trackVerificationContactUs()
    window.location.href = callCenterPhoneNumber
  }

  return (
    <FormLayout>
      <AccountHeader
        alignItems='start'
        title={header}
        description={subHeader}
        style={{ marginBottom: '0rem' }}
      />
      {failType === 'unverified' && (
        <InfoBox color='blue' size='sm'>
          <div>
            Please use reference <b>#{applicationReferenceNumber}</b> and have a suitable ID at hand
            for the call.
          </div>
        </InfoBox>
      )}

      <footer style={{ rowGap: spacing.sm }}>
        {failType === 'closedAccount' && (
          <Button isFullWidth onClick={contactUs}>
            Call now
          </Button>
        )}

        {failType === 'duplicateAccount' && (
          <>
            <Button isFullWidth onClick={() => showLogin({ onLoginSuccessRedirectUrl: '/' })}>
              Login
            </Button>
            <Button color='tertiary_color' isFullWidth onClick={contactUs}>
              Call support team
            </Button>
          </>
        )}

        {failType === 'sanctioned' && (
          <Button isFullWidth onClick={() => navigate('/')}>
            Home
          </Button>
        )}

        {failType === 'unverified' && (
          <>
            <Button
              isFullWidth
              onClick={() => {
                navigate(routes.verificationOptions)
              }}
            >
              Try another ID document
            </Button>
            <Button color='tertiary_color' isFullWidth onClick={contactUs}>
              Call now
            </Button>
          </>
        )}
        {failType === 'newUserAutoLogInFail' && (
          <>
            <Button isFullWidth onClick={() => showLogin({ onLoginSuccessRedirectUrl: '/' })}>
              Login
            </Button>
            <Button color='tertiary_color' isFullWidth onClick={contactUs}>
              Call support team
            </Button>
          </>
        )}
      </footer>
    </FormLayout>
  )
}
